/**
 * Configurator Frontend Initialization
 *
 * @package
 */

import { createRoot } from '@wordpress/element';
import App from './components/App.jsx';
import { prefetchProducts, setVehicleConfigId } from './helpers/useApiCache';

/**
 * Collect every product ID referenced by the block config (base models,
 * product groups and their sub-groups) for one up-front batch fetch.
 *
 * @param {Object} config Parsed data-config object.
 * @return {Array<number>} Product IDs.
 */
const collectProductIds = config => {
	const ids = [];

	( config.baseModels || [] ).forEach( baseModel => {
		ids.push( typeof baseModel === 'object' ? baseModel.product_id : baseModel );
	} );

	( config.productGroups || [] ).forEach( group => {
		( group.products || [] ).forEach( id => ids.push( id ) );
		( group.sub_groups || [] ).forEach( subGroup => {
			( subGroup.products || [] ).forEach( id => ids.push( id ) );
		} );
	} );

	return ids;
};

document.addEventListener( 'DOMContentLoaded', () => {
	const containers = document.querySelectorAll( '[id^="configurator-app"]' );
	let mountedVehicleConfigId = null;

	containers.forEach( container => {
		const configData = container.getAttribute( 'data-config' );
		if ( ! configData ) {
			return;
		}

		try {
			const config = JSON.parse( configData );

			// The vehicle is module-level state in the API cache, so a second configurator
			// for a different vehicle would fetch products against the wrong one.
			if ( mountedVehicleConfigId !== null && mountedVehicleConfigId !== config.vehicleConfigId ) {
				// eslint-disable-next-line no-console
				console.error( 'Only one vehicle configurator is supported per page; skipping the extra block.' );
				return;
			}

			// Set before any fetch: product requests name the vehicle so the
			// response carries fitting hours for this model.
			setVehicleConfigId( config.vehicleConfigId );
			mountedVehicleConfigId = config.vehicleConfigId;
			// Warm the product cache in a few batch requests before the cards
			// mount, so they don't each fire their own REST request.
			prefetchProducts( collectProductIds( config ) );
			const root = createRoot( container );
			root.render( <App config={ config } /> );
		} catch ( error ) {
			// eslint-disable-next-line no-console
			console.error( 'Failed to parse configurator config:', error );
		}
	} );
} );
